import { easeCubicInOut } from 'd3-ease';
import { interpolate } from 'd3-interpolate';
import { select } from 'd3-selection';
import 'd3-transition';
import { curveCardinal, line as d3Line } from 'd3-shape';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef } from 'react';

import { CHART_TRANSITION_MS, getBaselinePy, toPixelPoint } from '../utils/chartTransition';

function buildLinePath(points, tension) {
  if (points.length < 2) {
    return null;
  }
  return d3Line()
    .x(d => d.px)
    .y(d => d.py)
    .curve(curveCardinal.tension(tension))(points);
}

function toPixelPoints(points, xScaleSet, yScaleSet) {
  return points.map(pt => toPixelPoint(pt.x, pt.y, xScaleSet, yScaleSet));
}

function AnimatedLinePath({
  points,
  xScaleSet,
  yScaleSet,
  tension,
  animate,
  className,
  stroke,
  strokeWidth,
  strokeDasharray,
  strokeLinecap,
  style,
}) {
  const pathRef = useRef(null);
  const prevPointsRef = useRef(null);

  useLayoutEffect(() => {
    const pathEl = pathRef.current;
    if (!pathEl) {
      return;
    }

    const next = toPixelPoints(points, xScaleSet, yScaleSet);
    const prev = prevPointsRef.current;
    const linePath = buildLinePath(next, tension);

    if (
      prev &&
      prev.length === next.length &&
      prev.every((p, i) => Math.abs(p.px - next[i].px) < 0.01 && Math.abs(p.py - next[i].py) < 0.01)
    ) {
      return;
    }

    if (!linePath) {
      select(pathEl).attr('d', null);
      prevPointsRef.current = next;
      return;
    }

    if (!animate) {
      select(pathEl).interrupt().attr('d', linePath).attr('opacity', 1);
      prevPointsRef.current = next;
      return;
    }

    if (!prev || prev.length === 0) {
      const baselinePy = getBaselinePy(yScaleSet);
      const fromPoints = next.map(p => ({ px: p.px, py: baselinePy }));

      select(pathEl)
        .attr('d', buildLinePath(fromPoints, tension))
        .attr('opacity', 0)
        .transition()
        .duration(CHART_TRANSITION_MS)
        .ease(easeCubicInOut)
        .attr('opacity', 1)
        .attrTween('d', () => {
          const interpolators = next.map(target => ({
            px: interpolate(target.px, target.px),
            py: interpolate(baselinePy, target.py),
          }));
          return t =>
            buildLinePath(
              interpolators.map(({ px, py }) => ({ px: px(t), py: py(t) })),
              tension,
            );
        });

      prevPointsRef.current = next;
      return;
    }

    const prevByTime = new Map(prev.map(p => [p.xTime, p]));
    const baselinePy = getBaselinePy(yScaleSet);
    const sameLength = prev.length === next.length;

    const interpolators = next.map((target, i) => {
      const indexPrev = prev[i];
      const timeMatch = prevByTime.get(target.xTime);
      let from;

      if (sameLength && indexPrev) {
        if (indexPrev.xTime === target.xTime) {
          from = indexPrev;
        } else {
          from = { px: target.px, py: indexPrev.py };
        }
      } else if (timeMatch) {
        from = timeMatch;
      } else if (indexPrev) {
        from = { px: target.px, py: indexPrev.py };
      } else {
        from = { px: target.px, py: baselinePy };
      }

      return {
        px: interpolate(from.px, target.px),
        py: interpolate(from.py, target.py),
      };
    });

    select(pathEl)
      .interrupt()
      .transition()
      .duration(CHART_TRANSITION_MS)
      .ease(easeCubicInOut)
      .attrTween('d', () => t => {
        const frame = interpolators.map(({ px, py }) => ({
          px: px(t),
          py: py(t),
        }));
        return buildLinePath(frame, tension);
      });

    prevPointsRef.current = next;
  }, [points, xScaleSet, yScaleSet, tension, animate]);

  return (
    <path
      ref={pathRef}
      className={className}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeDasharray={strokeDasharray}
      strokeLinecap={strokeLinecap}
      style={style}
    />
  );
}

AnimatedLinePath.propTypes = {
  points: PropTypes.arrayOf(PropTypes.object).isRequired,
  xScaleSet: PropTypes.func.isRequired,
  yScaleSet: PropTypes.func.isRequired,
  tension: PropTypes.number,
  animate: PropTypes.bool,
  className: PropTypes.string,
  stroke: PropTypes.string,
  strokeWidth: PropTypes.number,
  strokeDasharray: PropTypes.string,
  strokeLinecap: PropTypes.string,
  style: PropTypes.object,
};

AnimatedLinePath.defaultProps = {
  tension: 0.85,
  strokeLinecap: 'round',
  animate: true,
};

export default AnimatedLinePath;
